import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import Tooltip from "@mui/material/Tooltip";
import calendar from "../../../assets/images/calendar.svg";

const Toggle = ({ value, handleToggleChange, label, required }) => (
  <div className="toggleDiv">
    <label className="input-text-label">
      {label}
      {required && <span style={{ color: "red" }}>*</span>}
    </label>
    <div className="custom-toggle-wrapper" onClick={handleToggleChange}>
      <input
        type="checkbox"
        className="toggle-input"
        checked={value || false}
        readOnly
      />
      <label className="toggle-label">
        <span className="toggle-text yes" style={{ opacity: value ? 1 : 0 }}>
          Yes
        </span>
        <span className="toggle-text no" style={{ opacity: value ? 0 : 1 }}>
          No
        </span>
        <span className="toggle-circle">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21"
            height="22"
            viewBox="0 0 21 22"
            fill="none"
          >
            <circle cx="10.5" cy="10.5645" r="10.5" fill="#2F80ED" />
          </svg>
        </span>
      </label>
    </div>
    <style jsx="true">{`
      .custom-toggle-wrapper {
        position: relative;
        display: inline-block;
        cursor: pointer;
      }
      .toggle-input {
        display: none;
      }
      .toggle-label {
        display: inline-block;
        width: 60px;
        height: 26px;
        border-radius: 22px;
        border: 2px solid #2f80ed;
        background: #fff;
        position: relative;
        transition: all 0.3s ease;
      }
      .toggle-text {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        font-size: 10px;
        font-weight: 500;
        color: #000;
        transition: opacity 0.3s ease;
        pointer-events: none;
      }
      .toggle-text.yes {
        left: 8px;
      }
      .toggle-text.no {
        right: 8px;
      }
      .toggle-circle {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        transition: all 0.3s ease;
        left: ${value ? "35px" : "2px"};
        pointer-events: none;
      }
    `}</style>
  </div>
);

function InputComponent({
  config,
  options = {},
  value,
  onChange,
  onSelect,
  customWidth,
}) {
  const {
    label,
    type,
    placeholder,
    options: optionsKey,
    required,
    disabled,
    tooltip,
  } = config;
  const selectOptions =
    optionsKey && options[optionsKey] ? options[optionsKey] : optionsKey;
  const isSuggestableText = type === "text" && Array.isArray(selectOptions);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchValue, setSearchValue] = useState(
    typeof value === "string" ? value : ""
  );
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectHoveredIndex, setSelectHoveredIndex] = useState(-1);
  const [selectFocusedIndex, setSelectFocusedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    positionAbove: false,
  });
  const [isInsidePopup, setIsInsidePopup] = useState(false);
  const suggestionsId = useRef(
    `dynamic-input-suggestions-${Math.random().toString(36).slice(2)}`
  );
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const selectListId = useRef(
    `dynamic-select-options-${Math.random().toString(36).slice(2)}`
  );
  const selectToggleRef = useRef(null);
  const selectOptionsRef = useRef(null);
  const selectContainerRef = useRef(null);
  const optionRefs = useRef([]);
  const labelId = useRef(
    `dynamic-input-label-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    if (!showSuggestions) {
      setHoveredIndex(-1);
      setFocusedIndex(-1);
    }
  }, [showSuggestions]);

  useEffect(() => {
    if (!isSuggestableText) return;
    setSearchValue(typeof value === "string" ? value : "");
  }, [value, isSuggestableText]);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  const normalizedSelectOptions = useMemo(
    () => (Array.isArray(selectOptions) ? selectOptions : []),
    [selectOptions]
  );

  const filteredOptions = useMemo(() => {
    if (!isSuggestableText) return [];
    const query = searchValue.trim().toLowerCase();
    if (!query) return normalizedSelectOptions;
    const startsWith = [];
    const contains = [];
    normalizedSelectOptions.forEach((option) => {
      const labelText = String(option?.label ?? "").toLowerCase();
      if (labelText.startsWith(query)) {
        startsWith.push(option);
      } else if (labelText.includes(query)) {
        contains.push(option);
      }
    });
    return [...startsWith, ...contains];
  }, [normalizedSelectOptions, searchValue, isSuggestableText]);

  const selectedOption = useMemo(() => {
    return normalizedSelectOptions.find((option) => option.value === value);
  }, [normalizedSelectOptions, value]);

  const selectDisplayLabel = selectedOption
    ? selectedOption.label
    : value || placeholder;
  const isSelectShowingPlaceholder = !selectedOption && !value;

  const handleInputChange = (e) => {
    const nextValue = e?.target?.value ?? e?.value ?? "";
    if (onChange) onChange(e);
    if (isSuggestableText) {
      setSearchValue(nextValue);
      setShowSuggestions(true);
    }
  };

  const commitSelection = useCallback(
    (suggestion) => {
      const syntheticEvent = {
        target: { value: suggestion.value, name: label },
        value: suggestion.value,
        label: suggestion.label,
      };
      onChange && onChange(syntheticEvent);
      onSelect && onSelect(suggestion);
      setShowSuggestions(false);
      if (isSuggestableText) {
        setSearchValue(String(suggestion.value ?? ""));
      }
      setHoveredIndex(-1);
      setFocusedIndex(-1);
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    [isSuggestableText, onChange, onSelect]
  );

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleSelectCommit = useCallback(
    (option) => {
      if (!option) return;
      const syntheticEvent = {
        target: { value: option.value, name: label },
        value: option.value,
        label: option.label,
      };
      onChange && onChange(syntheticEvent);
      onSelect && onSelect(option);
      setIsSelectOpen(false);
      setSelectFocusedIndex(-1);
      setSelectHoveredIndex(-1);
      requestAnimationFrame(() => selectToggleRef.current?.focus());
    },
    [label, onChange, onSelect]
  );

  const handleSelectToggle = () => {
    if (disabled) return;
    setIsSelectOpen((prev) => {
      const next = !prev;
      if (!next) {
        setSelectFocusedIndex(-1);
        setSelectHoveredIndex(-1);
      }
      return next;
    });
  };

  const handleSelectKeyDown = (event) => {
    if (disabled || !isSelectOpen) {
      if ((event.key === "ArrowDown" || event.key === "Enter") && !disabled) {
        event.preventDefault();
        setIsSelectOpen(true);
        setSelectFocusedIndex((prev) => {
          if (prev >= 0) return prev;
          const currentIndex = normalizedSelectOptions.findIndex(
            (option) => option.value === value
          );
          return currentIndex >= 0 ? currentIndex : 0;
        });
        setSelectHoveredIndex((prev) => {
          if (prev >= 0) return prev;
          const currentIndex = normalizedSelectOptions.findIndex(
            (option) => option.value === value
          );
          return currentIndex >= 0 ? currentIndex : -1;
        });
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectFocusedIndex((prev) => {
        const nextIndex =
          prev + 1 < normalizedSelectOptions.length ? prev + 1 : 0;
        setSelectHoveredIndex(nextIndex);
        return nextIndex;
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectFocusedIndex((prev) => {
        const nextIndex =
          prev - 1 >= 0 ? prev - 1 : normalizedSelectOptions.length - 1;
        setSelectHoveredIndex(nextIndex);
        return nextIndex;
      });
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option =
        normalizedSelectOptions[
          selectFocusedIndex >= 0 ? selectFocusedIndex : 0
        ];
      if (option) {
        handleSelectCommit(option);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsSelectOpen(false);
      setSelectFocusedIndex(-1);
      setSelectHoveredIndex(-1);
    }
  };

  const handleToggleChange = () => {
    if (onChange) {
      const newValue = !value;
      onChange({
        target: {
          checked: newValue,
          value: newValue,
          type: "checkbox",
        },
      });
    }
  };

  const handleDateKeyDown = (e) => {
    e.preventDefault();
  };

  const handleTextKeyDown = (event) => {
    if (!isSuggestableText || !showSuggestions || !filteredOptions.length)
      return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusedIndex((prev) => {
        const nextIndex = prev + 1 < filteredOptions.length ? prev + 1 : 0;
        setHoveredIndex(nextIndex);
        return nextIndex;
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedIndex((prev) => {
        const nextIndex = prev - 1 >= 0 ? prev - 1 : filteredOptions.length - 1;
        setHoveredIndex(nextIndex);
        return nextIndex;
      });
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[focusedIndex >= 0 ? focusedIndex : 0];
      if (option) {
        commitSelection(option);
      }
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const shouldShowNoResults =
    isSuggestableText &&
    searchValue.trim() !== "" &&
    filteredOptions.length === 0;

  const shouldShowSelectOptions = type === "select" && isSelectOpen;

  useEffect(() => {
    if (!shouldShowSelectOptions) return undefined;
    const handleClickOutside = (event) => {
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(event.target)
      ) {
        setIsSelectOpen(false);
        setSelectFocusedIndex(-1);
        setSelectHoveredIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [shouldShowSelectOptions]);

  useEffect(() => {
    if (!isSelectOpen) return;
    const currentIndex = normalizedSelectOptions.findIndex(
      (option) => option.value === value
    );
    if (normalizedSelectOptions.length === 0) {
      setSelectFocusedIndex(-1);
      setSelectHoveredIndex(-1);
      return;
    }
    setSelectFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
    setSelectHoveredIndex(currentIndex >= 0 ? currentIndex : -1);
    optionRefs.current = [];
  }, [isSelectOpen, normalizedSelectOptions, value]);

  // Detect if inside popup and calculate position for fixed positioning
  useEffect(() => {
    if (!isSelectOpen || !selectToggleRef.current) return;

    const updatePosition = () => {
      const toggleElement = selectToggleRef.current;
      if (!toggleElement) return;

      const popupElement = toggleElement.closest(".popup");
      const isInPopup = !!popupElement;

      setIsInsidePopup(isInPopup);

      if (isInPopup && toggleElement) {
        const rect = toggleElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const maxDropdownHeight = Math.min(
          40 * 5,
          40 * normalizedSelectOptions.length
        ); // Estimate ~40px per option, max 5 visible
        const minDropdownHeight = 200; // Minimum height to show

        // Check if we should position above instead of below
        // Position above if there's not enough space below AND more space above
        const shouldPositionAbove =
          spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;

        let topPosition;
        let maxHeight;

        if (shouldPositionAbove) {
          // Position above: use available space above, but ensure it doesn't go above viewport
          const availableSpace = Math.min(spaceAbove - 4, maxDropdownHeight);
          topPosition = Math.max(4, rect.top - availableSpace - 4);
          maxHeight = Math.min(availableSpace, maxDropdownHeight);
        } else {
          // Position below: use available space below
          const availableSpace = Math.min(spaceBelow - 4, maxDropdownHeight);
          topPosition = rect.bottom + 4;
          maxHeight = Math.max(availableSpace, minDropdownHeight);
        }

        setDropdownPosition({
          top: topPosition,
          left: rect.left,
          width: rect.width,
          positionAbove: shouldPositionAbove,
          maxHeight: maxHeight,
        });
      }
    };

    updatePosition();

    // Update position on scroll and resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isSelectOpen, normalizedSelectOptions.length]);

  useEffect(() => {
    if (!isSelectOpen) return;
    const activeIndex = selectFocusedIndex >= 0 ? selectFocusedIndex : 0;
    const activeOption = optionRefs.current?.[activeIndex];
    if (activeOption && typeof activeOption.focus === "function") {
      activeOption.focus();
      activeOption.scrollIntoView({ block: "nearest" });
    } else if (selectToggleRef.current) {
      selectToggleRef.current.focus();
    }
  }, [isSelectOpen, selectFocusedIndex]);

  const handleOptionKeyDown = (event, index) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectFocusedIndex((prev) => {
        const nextIndex =
          prev + 1 < normalizedSelectOptions.length ? prev + 1 : 0;
        setSelectHoveredIndex(nextIndex);
        return nextIndex;
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectFocusedIndex((prev) => {
        const nextIndex =
          prev - 1 >= 0 ? prev - 1 : normalizedSelectOptions.length - 1;
        setSelectHoveredIndex(nextIndex);
        return nextIndex;
      });
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option = normalizedSelectOptions[index];
      if (option) {
        handleSelectCommit(option);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsSelectOpen(false);
      setSelectFocusedIndex(-1);
      setSelectHoveredIndex(-1);
    }
  };

  const handleSelectBlur = () => {
    requestAnimationFrame(() => {
      const containerElement = selectContainerRef.current;
      if (!containerElement) return;
      const activeElement = document.activeElement;
      if (!activeElement) return;
      if (!containerElement.contains(activeElement)) {
        setIsSelectOpen(false);
        setSelectFocusedIndex(-1);
        setSelectHoveredIndex(-1);
      }
    });
  };

  return (
    <>
      {type === "text" &&
        (disabled ? (
          <Tooltip title={tooltip} followCursor>
            <div
              className={`input-text-wrapper ${customWidth ? customWidth : ""}`}
            >
              <label className="input-text-label">
                {label}
                {required && <span style={{ color: "red" }}>*</span>}
              </label>
              <input
                ref={inputRef}
                className="input-text-input"
                type={type}
                placeholder={placeholder}
                value={isSuggestableText ? searchValue : value}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={disabled}
                style={{ cursor: "not-allowed" }}
                role={isSuggestableText ? "combobox" : undefined}
                aria-expanded={isSuggestableText ? showSuggestions : undefined}
                aria-controls={
                  isSuggestableText ? suggestionsId.current : undefined
                }
                aria-autocomplete={isSuggestableText ? "list" : undefined}
                aria-haspopup={isSuggestableText ? "listbox" : undefined}
              />
            </div>
          </Tooltip>
        ) : (
          <div
            className={`input-text-wrapper ${customWidth ? customWidth : ""}`}
          >
            <label className="input-text-label">
              {label}
              {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              ref={inputRef}
              className="input-text-input"
              type={type}
              placeholder={placeholder}
              value={isSuggestableText ? searchValue : value}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleTextKeyDown}
              disabled={disabled}
              role={isSuggestableText ? "combobox" : undefined}
              aria-expanded={isSuggestableText ? showSuggestions : undefined}
              aria-controls={
                isSuggestableText ? suggestionsId.current : undefined
              }
              aria-autocomplete={isSuggestableText ? "list" : undefined}
              aria-haspopup={isSuggestableText ? "listbox" : undefined}
              aria-activedescendant={
                isSuggestableText && focusedIndex >= 0
                  ? `${suggestionsId.current}-option-${focusedIndex}`
                  : undefined
              }
            />
            {isSuggestableText && showSuggestions && (
              <div
                ref={suggestionsRef}
                className="suggestions-list"
                id={suggestionsId.current}
                role="listbox"
                onMouseDown={(e) => e.preventDefault()}
              >
                {shouldShowNoResults ? (
                  <div className="suggestion-item">No results found</div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <div
                      key={`${option.value}-${index}`}
                      id={`${suggestionsId.current}-option-${index}`}
                      className={`suggestion-item ${
                        hoveredIndex === index ? "is-hovered" : ""
                      }`}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        commitSelection(option);
                      }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(-1)}
                      role="option"
                      aria-selected={focusedIndex === index}
                      tabIndex={-1}
                    >
                      {option.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      {type === "textarea" && (
        <div className="input-textArea-wrapper">
          <label className="input-text-label">
            {label}
            {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <textarea
            className="input-textArea-input"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
          />
        </div>
      )}
      {type === "select" &&
        (disabled ? (
          <Tooltip title={tooltip} followCursor>
            <div
              className={`input-text-wrapper ${customWidth ? customWidth : ""}`}
            >
              <label className="input-text-label" id={labelId.current}>
                {label}
                {required && <span style={{ color: "red" }}>*</span>}
              </label>
              <div
                ref={selectContainerRef}
                className={`custom-select ${disabled ? "is-disabled" : ""}`}
                aria-disabled="true"
              >
                <div className="custom-select-display" aria-hidden="true">
                  {selectDisplayLabel}
                </div>
              </div>
            </div>
          </Tooltip>
        ) : (
          <div
            className={`input-text-wrapper ${customWidth ? customWidth : ""}`}
          >
            <label className="input-text-label" id={labelId.current}>
              {label}
              {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <div className="custom-select" ref={selectContainerRef}>
              <button
                type="button"
                className="custom-select-toggle"
                ref={selectToggleRef}
                onClick={handleSelectToggle}
                onKeyDown={handleSelectKeyDown}
                onBlur={handleSelectBlur}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isSelectOpen}
                aria-controls={selectListId.current}
                aria-labelledby={labelId.current}
                aria-activedescendant={
                  isSelectOpen && selectFocusedIndex >= 0
                    ? `${selectListId.current}-option-${selectFocusedIndex}`
                    : undefined
                }
                aria-disabled={disabled || undefined}
              >
                <span
                  className={`custom-select-value ${
                    isSelectShowingPlaceholder ? "is-placeholder" : ""
                  }`}
                >
                  {selectDisplayLabel}
                </span>
                <span aria-hidden="true" className="custom-select-icon">
                  ▾
                </span>
              </button>
              {shouldShowSelectOptions && (
                <div
                  ref={selectOptionsRef}
                  className={`custom-select-options ${
                    isInsidePopup ? "is-fixed-position" : ""
                  } ${
                    isInsidePopup && dropdownPosition.positionAbove
                      ? "is-positioned-above"
                      : ""
                  }`}
                  role="listbox"
                  id={selectListId.current}
                  style={
                    isInsidePopup
                      ? {
                          position: "fixed",
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                          width: `${dropdownPosition.width}px`,
                          maxHeight: dropdownPosition.maxHeight
                            ? `${dropdownPosition.maxHeight}px`
                            : undefined,
                        }
                      : undefined
                  }
                  aria-activedescendant={
                    selectFocusedIndex >= 0
                      ? `${selectListId.current}-option-${selectFocusedIndex}`
                      : undefined
                  }
                >
                  {normalizedSelectOptions.map((option, index) => (
                    <button
                      type="button"
                      key={`${option.value}-${index}`}
                      id={`${selectListId.current}-option-${index}`}
                      className={`custom-select-option ${
                        selectHoveredIndex === index ? "is-hovered" : ""
                      } ${selectFocusedIndex === index ? "is-focused" : ""}`}
                      role="option"
                      aria-selected={value === option.value}
                      onMouseEnter={() => setSelectHoveredIndex(index)}
                      onMouseLeave={() => setSelectHoveredIndex(-1)}
                      onClick={() => handleSelectCommit(option)}
                      onBlur={handleSelectBlur}
                      onKeyDown={(event) => handleOptionKeyDown(event, index)}
                      ref={(el) => {
                        if (el) optionRefs.current[index] = el;
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      {type === "date" && (
        <div className="input-text-wrapper">
          <label className="input-text-label">
            {label}
            {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <div className="custom-date-wrapper-event-form">
            <input
              className="custom-date-input"
              type="date"
              placeholder={placeholder}
              value={value}
              onChange={handleInputChange}
              onFocus={(e) => e.target.showPicker()}
              onKeyDown={handleDateKeyDown}
              min={new Date().toISOString().slice(0, 10)}
              max="9999-12-31"
            />
            <img
              src={calendar}
              alt="Calendar Icon"
              className="custom-date-icon"
              onClick={(e) => {
                const input = e.target.previousSibling;
                input.showPicker();
              }}
            />
          </div>
        </div>
      )}
      {type === "time" && (
        <div className="input-text-wrapper">
          <label className="input-text-label">
            {label}
            {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <div className="input-time-wrapper">
            <input
              type="time"
              className="input-time-input1"
              value={value}
              onChange={onChange}
              onFocus={(e) => e.target.showPicker()}
            />
            <select
              className="input-time-input2"
              onChange={onChange}
              value={value}
            >
              <option value="IST">IST</option>
            </select>
          </div>
        </div>
      )}
      {type === "toggle" && (
        <Toggle
          value={value}
          handleToggleChange={handleToggleChange}
          label={label}
          required={required}
        />
      )}
    </>
  );
}

export default InputComponent;
